/**
 * Story templates for the picture-book creation flow (comic engine).
 *
 * Wraps UNIFIED_STORY_TEMPLATES without changing it (the live old wizard uses it).
 * Two look-alike templates are rewritten here, for the new flow only.
 * Never import this from worker-reachable files.
 */
import {
  UNIFIED_STORY_TEMPLATES,
  personalizeTitle,
  filterCommentsForCharacterCount,
  type UnifiedStoryTemplate,
} from '@/lib/unified-story-templates';
// Type only (erased): the master-script data itself is server-only and is never imported here
import type { MasterScript } from '@/types/master-script';
import { qaMasterIds, type QaEnv } from './qa-masters';
import { BABY_ROLE_TEMPLATES, castBabyRoleStory } from './casting';

// storyContext is partial: it merges over the template's own (pictureBookTemplates), so the result is always complete
type OverrideFields = Omit<
  Pick<UnifiedStoryTemplate, 'titleSingular' | 'titlePlural' | 'description' | 'storyContext' | 'antagonist' | 'hasSuperPowers'>,
  'storyContext'
> & { storyContext: Partial<UnifiedStoryTemplate['storyContext']> };

export const PICTURE_BOOK_TEMPLATE_OVERRIDES: Record<string, OverrideFields> = {
  'wizard-school': {
    titleSingular: '[[NAME]] at the Lantern Academy',
    titlePlural: '[[NAMES]] at the Lantern Academy',
    description: 'Open a glowing seed pod, find your lantern colour and learn cloud-weaving at a magical garden academy!',
    storyContext: {
      setting:
        'The Lantern Academy, a hilltop school of glass greenhouses and lantern-lit towers, where cloud-weaving classrooms drift above potion gardens and the library shelves rearrange themselves into maps',
      plotOutline:
        'An invitation arrives in a glowing seed pod. At the academy, students are grouped by the colour their lantern glows. Lessons are cloud-weaving and potion-gardening, and the library shelves rearrange into maps that lead to a hidden garden. Using what they learned, the hero helps the academy bloom again',
      additionalComments: `COSTUME: A short cape in the colour the hero's lantern glows, a seed-pod satchel at the hip, and a brass lantern carried on a cord. Everyone keeps their own face, skin tone, hair and features.
FOR MULTIPLE CHARACTERS: Each child's lantern glows a different colour (amber, teal, rose, violet, lime), and each short cape matches its lantern.

VISUAL MAGIC: Lanterns glow softly in the child's colour. Clouds are woven like thread on wooden looms. Potion-garden plants shimmer and unfurl.

EPIC SCENES: A glowing seed pod arriving at the window, the lantern choosing its colour, the library shelves rearranging into maps, a cloud-weaving lesson on the rooftop looms, a potion-gardening lesson among glowing plants, finding the hidden garden, and a joyful lantern-lit celebration.

TONE: Wonder, curiosity, friendship and gentle teamwork.`,
    },
    antagonist: {
      type: 'challenge',
      description: 'a hidden garden that has stopped blooming',
      threat: 'leaving the academy without its glow',
    },
    hasSuperPowers: false,
  },
  'save-the-planet': {
    titleSingular: '[[NAME]] and the Coral-Sky Kingdom',
    titlePlural: '[[NAMES]] and the Coral-Sky Kingdom',
    description: 'Visit a floating kingdom of crystal reefs, ride a gentle sky-manta ray and help save the glowing tide-pools!',
    storyContext: {
      setting:
        'The Coral-Sky Kingdom, a floating archipelago of crystal reefs high above the clouds, with glowing tide-pools, drifting coral gardens and gentle sky-manta rays',
      plotOutline:
        'The hero is welcomed as a royal guest of the Coral-Sky Kingdom, makes friends with a gentle sky-manta ray and rides it between the floating reefs. When the glowing tide-pools begin to dim, the hero and friends work together to bring their light back',
      additionalComments: `COSTUME: Royals wear shimmering scale-patterned capes over simple tunics. The cast keep their own skin tone and features on every page, with no change to their faces or bodies.
FOR MULTIPLE CHARACTERS: Each child wears a cape in a different shimmering colour (coral, sea-green, gold, silver, violet).

VISUAL MAGIC: Glowing tide-pools pulse with soft light. Crystal reefs catch the sunlight and throw coloured light.

EPIC SCENES: Arriving at the floating archipelago, being welcomed as royal guests, meeting a sky-manta ray, soaring between crystal reefs, discovering the dimmed tide-pools, restoring their glow, and a bright celebration on the reef.

TONE: Wonder, kindness, friendship and gentle adventure.`,
    },
    antagonist: {
      type: 'force_of_nature',
      description: 'tide-pools whose light is fading',
      threat: 'leaving the kingdom in the dark',
    },
    hasSuperPowers: false,
  },
  'holiday-adventure': {
    titleSingular: "[[NAME]]'s Big Holiday Adventure",
    titlePlural: "[[NAMES]]'s Big Holiday Adventure",
    description: 'A golden sleigh bell, a storm on the hill, and a whole family out on Christmas Eve to help Santa.',
    storyContext: {
      setting:
        'A small snowy town on Christmas Eve: a warm family home with stockings by the fire, and Holly Hill above the town, wild with snow once the storm comes in',
      plotOutline:
        'On Christmas Eve the hero, who has always wanted to see Santa just once, finds a golden sleigh bell in the snow and keeps it as proof. At night a little reindeer is lost in the storm without it, so the whole family climbs the hill together, each with a job, and the hero gives the bell back. Santa comes, thanks everyone, and the hero needs no proof',
    },
  },
  'new-sibling': {
    titleSingular: '[[NAME]] Becomes the Big One',
    titlePlural: '[[NAMES]] and the New Baby',
    description: 'A hat that is still mine, a drum too loud, and a song that used to be all mine: the littlest one becomes the big one.',
    storyContext: {
      setting:
        'A cosy family home getting ready for a new baby: pencil height marks on the doorframe, a little room with a cradle, a window seat for watching the street, the kitchen table with its long tablecloth, and the big sofa at home',
      plotOutline:
        'The littlest one in the family learns a baby is coming and wonders if there will still be room. The hat, the cradle and even the hero\'s own bedtime hush-a-hush song seem to go to the baby, and the hero\'s loud drum only makes the baby cry. When the tired grown-up yawns in the middle of the song, the hero chooses to sing the rest. The baby holds the hero\'s finger, the grown-up sings to "my little ones", and the hero draws the baby\'s new mark below their own',
    },
  },
  'grandparents-day': {
    titleSingular: "[[NAME]]'s Big Day at the Grandparents'",
    titlePlural: "[[NAME]]'s Big Day at the Grandparents'",
    description: 'A whole day with the grandparents, one treasure at a time from the old Remember Box, and a secret stripy stone in the hero\'s pocket.',
    storyContext: {
      setting:
        'The grandparents\' warm home: a flowery garden path and a blue front door, creaky attic stairs, a sunny attic with old trunks and a big old blue chest, an enormous apple tree in the garden, a kitchen table for supper and a big armchair under a lamp',
      plotOutline:
        'The hero runs up the garden path for a whole day with the grandparents, a secret stripy stone in one pocket, sure it beats anything in the old Remember Box. The box has one rule: one treasure at a time, the best one last, each showing a different side of the grandparent (a tiny shoe, a whistle, a patched teddy, red dancing shoes, a photo of the grandparent as a child planting the apple tree). The best treasure at the bottom is a photo of the grandparent holding the hero as a baby. The hero lays the stripy stone in the box beside it, unasked, and tells its story while the grandparent listens. Slow, slow, down the path, one hand on an empty pocket',
    },
  },
  'bedtime-moon': {
    titleSingular: '[[NAME]] and the Sleepy Moon',
    titlePlural: '[[NAMES]] and the Sleepy Moon',
    description: "Up a moonbeam to calm the sleepy Moon's wild stars, slow and low, and lend a bunny to the littlest one. Then home, and sleep.",
    storyContext: {
      setting:
        'A quiet bedroom at night that opens onto a gentle dreamland: a moonbeam path over a sleeping town, pillowy clouds in soft purples and blues, and the sleepy Moon\'s cosy crescent home',
      plotOutline:
        'The hero cannot fall asleep and keeps one eye on the dark corner. A moonbeam leads up to the sleepy Moon, whose stars will not settle. Chasing them only makes them wilder, so the hero sits still and sings the family moon song, slow and low, and the stars settle. The littlest star is still scared of the dark and the song is not enough, so the hero lends it Bun the bunny. Home without Bun, the hero lies in the dark until the littlest star comes to shine at the window, and falls asleep; while the hero sleeps, Bun comes home',
    },
  },
  'birthday-surprise': {
    titleSingular: 'Detective [[NAME]] and the Birthday Surprise',
    titlePlural: 'Detectives [[NAMES]] and the Birthday Surprise',
    description: "A birthday detective follows the clues to the family's secret surprise, and at the very last door, chooses not to peek.",
    storyContext: {
      setting: "A family home and garden on the hero's birthday, from first light to a night garden strung with lights, with a trip to the duck pond",
      plotOutline:
        'Nobody has ever surprised the hero, and on birthday morning Detective [[NAME]] sets out to crack the family\'s secret: a floury nose, a ladder and a tangle of lights in the tree, a bag that rattles, a map where every "Nothing to see here!" points to the garden. That night, hand on the back-door handle, the hero hears the family practising the big SURPRISE, and lets go. The garden blazes with lights, paper stars and a magnifying-glass cake, and the hero never tells how close it came.',
    },
  },
  'superheroes': {
    titleSingular: '[[NAME]] and the Superhero Squad',
    titlePlural: '[[NAMES]] and the Superhero Squad',
    description: 'A shooting star gives the whole family superpowers, all but one. Then a giant grey Gloom swallows the city, and only the hero with no power can walk in.',
    storyContext: {
      setting:
        'A bright little seaside city, the family home and park, and the inside of the Gloom: a colossal grey cloud that drains the colour from everything it touches',
      plotOutline:
        'A shooting star gives everyone in the family a superpower except the hero. When the Gloom rolls in from the sea, it drinks the colour and every power thrown at it only makes it bigger. Seeing that it is puffed up like a frightened cat, the hero walks in on a length of thick red wool with no power at all. At its heart a puppy-sized cloud is crying and clutching the hero\'s own lost star-piece; the hero reaches for it, sees it shake, and says "You keep it." The cloud calms and rains the stolen colour back, though the family\'s powers never come back. The hero never gets a power; next morning the grandparent stitches a cloud and a rainbow onto the hero\'s plain patch.',
    },
    antagonist: {
      type: 'force_of_nature',
      description: 'the Gloom, a vast grey cloud that drains the colour and laughter out of the city',
      threat: 'smothering the whole city in grey silence',
    },
    hasSuperPowers: true,
  },
  'dragon-riders': {
    titleSingular: '[[NAME]] the Dragon Rider',
    titlePlural: '[[NAME]] the Dragon Rider',
    description: 'An egg that has waited above the bakery oven hatches at dawn, a small dragon picks its rider, and summer\'s end asks the rider to let it fly alone.',
    storyContext: {
      setting:
        'A stone bakery at the very top of a steep mountain village, where the bread oven has never gone cold; the steep meadow behind it that runs down into the evening wind; and the open sky above the clouds',
      plotOutline:
        'A dragon egg has waited a hundred years on the warm shelf above the bakery oven. The hero says good morning to it every day, and one dawn it hatches and picks the hero as its rider by voice. Crumb cannot get off the ground until the hero works out the take-off, running downhill into the wind. The sky is suddenly very close. At summer\'s end the great sky dragons fly over, and Crumb stops looking up. The hero ties the red scarf round Crumb\'s neck, runs it down the meadow holding its tail, and lets go. After the long winter a dragon as big as the bakery comes back from the south, on its way north, for a visit: good morning, Crumb',
      additionalComments: `COSTUME: Warm everyday mountain-village clothes, a long red knitted scarf and old brass flying goggles. No armour, no weapons, no saddles, no Nordic or Viking styling.
DRAGON COMPANION: Crumb, a cream dragon with copper-brown speckles like a bird's egg, scaled not furry, golden wings, a soft frill and no tail fin, pony-sized in summer and bakery-sized by spring. Gentle and curious, never fierce; it breathes warm steam, not flames.`,
    },
    hasSuperPowers: false,
  },
  'first-day-school': {
    titleSingular: "[[NAME]]'s First Day of School",
    titlePlural: "[[NAME]]'s First Day of School",
    description: 'New shoes that squeak, a big gate, and a friend who has been looking for you too.',
    storyContext: {
      setting:
        'An ordinary first school day from morning to evening: the hall at home, a walk to a tall green school gate, a bright classroom with picture-labelled coat pegs by the door, a story carpet and a reading tent, and a playground. No particular country or school system',
      plotOutline:
        'On the first day of school the hero\'s brand-new shoes squeak, and the hero tiptoes all day so nobody will hear. Tiptoeing keeps the hero quiet but also unseen and left out. In the hush of story time a tiny squeak comes from under the coat pegs, and the hero chooses to stand up, put both heels down and squeak across the whole room to sit beside Ari, who has been looking for a friend too. Tomorrow is the second day, and somebody is listening',
      additionalComments: `COSTUME: Neat everyday clothes (a navy top with grey trousers or a grey skirt, not a uniform: no badge, crest or tie), a small blue backpack and brand-new bright yellow shoes with thick white soles. Ari wears a red raincoat.
TONE: Gentle, funny and warm; nobody is unkind.`,
    },
    hasSuperPowers: false,
  },
  'diwali-lights': {
    titleSingular: '[[NAME]] and the Festival of Lights',
    titlePlural: '[[NAMES]] and the Festival of Lights',
    description: 'A painted diya, a flower rangoli and a dark window across the lane: the lights come one window at a time.',
    storyContext: {
      setting:
        'A quiet lane of two-storey homes on Diwali: the family\'s front step, kitchen and rangoli, and the old house directly across the lane. Sunny by day, dusk, then night with lamps',
      plotOutline:
        'Every year old Mr Joshi across the lane lights the first diya on the street, and this year the hero has painted a lamp to do it. The hero helps with the flower rangoli and shares laddoos, and learns that Mr Joshi\'s family cannot come this year and his lamps are tied up in their box. At the rangoli with the matches ready, the hero carries the painted diya across the lane instead. The lights come one window at a time, and the first one burns on Mr Joshi\'s step',
      additionalComments: `COSTUME: Everyday clothes by day, then new festive clothes in jewel colours with gold embroidery as suits each family (kurta, lehenga, salwar kameez, sherwani, pattu pavadai, veshti or silk dhoti, silk saree or a festive dress). No religious symbols (no deity images, Om, swastik or Khanda motifs), no written text or banners.
DIWALI ELEMENTS: Clay diyas with small steady flames, a flower rangoli of coloured powder and petals, marigold garlands, a plate of sweets shared with neighbours. No worship or deities shown. One sparkler, held at arm's length by a grown-up with a bucket of water beside; the children only watch; no fireworks.`,
    },
    hasSuperPowers: false,
  },
  'pirate-adventure': {
    titleSingular: 'Captain [[NAME]] and the Pirate Treasure Hunt',
    titlePlural: 'Captains [[NAMES]] and the Pirate Treasure Hunt',
    description: 'Half a treasure map, a grumpy rival with the other half, and a storm that asks a young captain what winning is worth.',
    storyContext: {
      setting:
        'The high seas aboard the Jolly Kipper and Whistle Island, with its jungle, cliffs, sea cave and a tall black rock that whistles just before every gust',
      plotOutline:
        'The young captain finds half of Captain Pebble\'s treasure map in a bottle: the way in, but no X. Grumpy Captain Gristle, who never shares, has the half with the X, and races the hero to Whistle Island. When a storm strands his crew on a rock as the tide rises, the hero refuses to trade the rope for his half and saves them anyway, timing the throw to the rock\'s whistle. Gristle, who always pays his debts, hands over his half: the X is on that very rock. The hero shares the gold equally, and the rivals part with a race home and a new half map in a bottle.',
    },
    antagonist: {
      type: 'rival',
      description: 'Captain Gristle, a grumpy, sly rival pirate who holds the other half of the map; he never shares, but he always pays his debts',
      threat: 'racing to find the treasure first and keeping it all',
    },
    hasSuperPowers: false,
  },
};

/**
 * Overrides drafted with the 2026-10 master scripts stay inert until the owner approves that title's script
 * (gate A of the story-catalogue plan): remove an id from this set when its master is approved.
 */
export const OVERRIDES_PENDING_APPROVAL: ReadonlySet<string> = new Set([
  'holiday-adventure',
  // Approved by the owner 2026-10-30 (production QA read-aloud): new-sibling, bedtime-moon, pirate-adventure.
  // Approved by the owner 2026-11-03 (batch 1 read-aloud): grandparents-day, birthday-surprise, superheroes,
  // dragon-riders, first-day-school, diwali-lights.
]);

/**
 * Overrides that have a catalogue master script (content/catalogue/scripts MASTER_SCRIPTS, server-only data, so the
 * ids are listed here; a test keeps the two in step). wizard-school and save-the-planet are overrides with no master.
 */
export const PICTURE_BOOK_MASTER_IDS: ReadonlySet<string> = new Set([
  'holiday-adventure', 'new-sibling', 'grandparents-day', 'bedtime-moon', 'birthday-surprise', 'superheroes',
  'dragon-riders', 'first-day-school', 'diwali-lights', 'pirate-adventure',
]);

/**
 * Client-safe: picking this template makes a master book, whose title is the catalogue title (the parent cannot
 * change it). True while the master is live: approved (its override is live), or a dev QA master.
 */
export function templateTitleIsFixed(id: string | undefined | null, env?: QaEnv): boolean {
  return !!id && PICTURE_BOOK_MASTER_IDS.has(id) && !!activeOverride(id, env);
}

/** The live override for a template, or undefined (pending ones are not live). */
export function activeOverride(id: string, env?: QaEnv): OverrideFields | undefined {
  if (OVERRIDES_PENDING_APPROVAL.has(id) && !qaMasterIds(env).has(id)) return undefined;
  return PICTURE_BOOK_TEMPLATE_OVERRIDES[id];
}

export function pictureBookTemplates(env?: QaEnv): UnifiedStoryTemplate[] {
  return UNIFIED_STORY_TEMPLATES.map((t) => {
    const o = activeOverride(t.id, env);
    // storyContext merges over the template's, so its additionalComments etc. survive unless the override sets them.
    return o ? { ...t, ...o, storyContext: { ...t.storyContext, ...o.storyContext } } : t;
  });
}

export function pictureBookTemplate(id: string, env?: QaEnv): UnifiedStoryTemplate | undefined {
  return pictureBookTemplates(env).find((t) => t.id === id);
}

type Person = { name: string; age: number | string; isMainCharacter?: boolean };

const ageOf = (p: Person): number => {
  const n = Number(p.age);
  return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
};

/**
 * Youngest child (age < 18) marked main, else youngest child, else the main character, else the first person.
 * With a baby-role template id (new-sibling), the casting rule of ./casting decides instead, so a newborn is never
 * the hero while an older child is in the cast; a cast with no possible hero (that template does not fit, see
 * pictureBookTemplateFitsCast) falls back to the rule above. Returns undefined for an empty array.
 */
export function pictureBookHero<T extends Person>(cs: T[], templateId?: string): T {
  if (!cs.length) return undefined as unknown as T;
  if (templateId && BABY_ROLE_TEMPLATES.has(templateId)) {
    const { hero } = castBabyRoleStory(cs);
    if (hero) return hero;
  }
  const kids = cs.filter((c) => ageOf(c) < 18);
  const youngest = (list: T[]) => list.reduce((a, b) => (ageOf(b) < ageOf(a) ? b : a));
  const mainKids = kids.filter((c) => c.isMainCharacter);
  if (mainKids.length) return youngest(mainKids);
  if (kids.length) return youngest(kids);
  return cs.find((c) => c.isMainCharacter) ?? cs[0];
}

/** At most 2 names in a title (spec 4.3): with 3-4 people, use the singular title with the hero's name. */
export function templateTitleFor(t: UnifiedStoryTemplate, cs: Person[]): string {
  // No people: the unpersonalized title.
  if (cs.length === 0) return t.titleSingular;
  // A baby-role story is the hero's: the title names the hero only, never the newborn
  const babyStory = BABY_ROLE_TEMPLATES.has(t.id);
  const hero = pictureBookHero(cs, t.id);
  if (cs.length <= 2 && !babyStory) return personalizeTitle(t, cs, hero.name);
  return t.titleSingular.replace(/\[\[NAME\]\]/g, () => hero.name);
}

/** English (any region tag): the catalogue titles are written in English, so only these books use them verbatim. */
export function catalogueTitleIsVerbatim(language: string | null | undefined): boolean {
  return String(language ?? '').trim().toLowerCase().split(/[-_]/)[0] === 'en';
}

/**
 * The cover/book title of a book stamped with a catalogue master: the catalogue override title filled from the cast
 * (templateTitleFor's rules: [[NAMES]] = every cast name in cast order for 1-2 people; 3+ people or a baby-role story
 * name the hero only). Read from PICTURE_BOOK_TEMPLATE_OVERRIDES directly, never through activeOverride: the master
 * stamp is for good, so a pending/QA-only override still titles the book it was stamped on. undefined when the id has
 * no override or there is no cast (the caller keeps its own title).
 */
export function masterTitleFor(masterId: string, cs: Person[]): string | undefined {
  const o = PICTURE_BOOK_TEMPLATE_OVERRIDES[masterId];
  const base = UNIFIED_STORY_TEMPLATES.find((t) => t.id === masterId);
  if (!o || !base || cs.length === 0) return undefined;
  return templateTitleFor({ ...base, titleSingular: o.titleSingular, titlePlural: o.titlePlural }, cs);
}

export function templateIdeaFor(t: UnifiedStoryTemplate): string {
  const full = `${t.description} ${t.storyContext.plotOutline}`;
  if (full.length <= 500) return full;
  const cut = full.slice(0, 500);
  const sp = cut.lastIndexOf(' ');
  // Cut at a word boundary unless the 500th char already ends a word.
  const base = full[500] === ' ' || sp < 0 ? cut : cut.slice(0, sp);
  return base.trimEnd();
}

export function templateBriefFor(t: UnifiedStoryTemplate, characterCount: number): string {
  const lines: string[] = [`Setting: ${t.storyContext.setting}`];
  const notes = filterCommentsForCharacterCount(t.storyContext.additionalComments, characterCount);
  lines.push(`Notes: ${notes}`);
  if (t.hazardOutfitDescription) lines.push(`Hazard outfits: ${t.hazardOutfitDescription}`);
  if (t.antagonist) {
    const a = t.antagonist;
    lines.push(`Challenge: ${a.name ?? 'The challenge'} — ${a.description}; ${a.threat}`);
  }
  return lines.join('\n').slice(0, 2500);
}

/** One registry line per key, each description cut to `max` chars. */
const keyLines = (rec: Record<string, string>, max: number): string =>
  Object.entries(rec).map(([k, v]) => `- ${k}: ${v.length > max ? `${v.slice(0, max - 1).trimEnd()}…` : v}`).join('\n');

/**
 * The pitch brief of an approved master script (story catalogue, P3): setting, logline, cover moment, costumes and
 * the master's location and recurring keys, so the pitch's registry uses the master's own keys. <= 2500 chars, like
 * templateBriefFor. `characterCount` says how many people the family has (every one gets a role in the master).
 */
export function masterBriefFor(m: MasterScript, characterCount: number): string {
  const head = [
    `Setting: ${m.setting}`,
    `Story: ${m.logline}`,
    `Cover: ${m.cover}`,
    // The cast is the whole family; the master's costume prose stays out (it reaches the pages through the adapt beats,
    // and in the brief it invited invented relatives into the pitch registry)
    `Cast: ${characterCount} ${characterCount === 1 ? 'person' : 'people'} from the family photo, and they are the whole family: never add a parent, grandparent, brother, sister or other relative. Supporting characters: only the recurring names below.`,
  ].join('\n');
  // The wardrobe goes on as costume DATA (never the costume prose: it named relatives): code fills it into every cast
  // member's registry entry, one colour per person in cast order, so the pitch never invents outfits of its own
  const w = m.wardrobe;
  const wardrobe = w?.base?.trim()
    ? [
      `Wardrobe (fixed costume data; code sets every cast outfit from it): ${ownColour(w.base)}`,
      ...(w.hero?.trim() ? [`Wardrobe for the main child only (instead of the above): ${ownColour(w.hero)}`] : []),
      ...(w.colours?.length ? [`Wardrobe colours, one per person in cast order: ${w.colours.join('; ')}`] : []),
      ...Object.entries(w.variants ?? {}).map(([k, v]) => `Wardrobe variant "${k}" (only on the pages that wear it): ${ownColour(v)}`),
      ...(m.physics?.length ? [`Physics the pictures obey: ${m.physics.join(', ')}`] : []),
    ].join('\n')
    : m.physics?.length ? `Physics the pictures obey: ${m.physics.join(', ')}` : '';
  const locs = Object.keys(m.locations).length
    ? `Locations (use exactly these location names as the registry location keys):\n${keyLines(m.locations, 110)}`
    : '';
  const rec = Object.keys(m.recurring).length
    ? `Recurring characters and props (use exactly these names as registry keys):\n${keyLines(m.recurring, 110)}`
    : '';
  // The cover, setting, story and cast (family) line come first, then the wardrobe, which is never dropped for the
  // registry lines: room is reserved for it, so the 2500 cap only ever drops whole recurring, then location, lines at
  // the end. With no wardrobe the brief is exactly as before
  const wardrobeText = wardrobe ? cappedLines(wardrobe.split('\n'), Math.max(0, 2500 - head.length - 1)) : '';
  const room = wardrobeText ? 2500 - wardrobeText.length - 1 : 2500;
  const out = cappedLines([head, locs, rec].filter(Boolean).join('\n').split('\n'), room);
  if (wardrobeText && out.startsWith(head)) return `${head}\n${wardrobeText}${out.slice(head.length)}`;
  return out || head.slice(0, 2500);
}

/** Whole lines joined by newlines while the result stays within `cap` characters. */
function cappedLines(lines: string[], cap: number): string {
  let out = '';
  for (const line of lines) {
    const next = out ? `${out}\n${line}` : line;
    if (next.length > cap) break;
    out = next;
  }
  return out;
}

/** A wardrobe text for the brief: {colour} dropped, plus "(each person in their own colour)" when it had one. */
function ownColour(text: string): string {
  const had = /\{colour\}/.test(text);
  const clean = text.replace(/\{colour\}-?\s*/g, '').replace(/\s+/g, ' ').trim();
  return had ? `${clean} (each person in their own colour)` : clean;
}

export function templateThumbnailFor(t: UnifiedStoryTemplate): string | null {
  return activeOverride(t.id) ? null : t.thumbnailUrl;
}
